import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  type WritableSignal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import {
  openRunTestCaseDialog,
  type RunTestCaseDialogData,
} from '../../../components/run-test-case-dialog/run-test-case-dialog.component';
import { TestCaseAutomationFramework } from '../../../models/test-case.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { copyText } from '../../../shared/clipboard';
import { formatJobDuration } from '../../../shared/duration.util';
import {
  ExecutionJob,
  ExecutionJobService,
  PollTimeoutError,
  cancelFailureKey,
  isFinalStatus,
  type CancelFailureKey,
} from '../../../services/execution-job.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subscription, finalize, map, of, switchMap, tap } from 'rxjs';

const DURATION_TICK_MS = 1000;

interface FrameworkOption {
  value: TestCaseAutomationFramework;
  labelKey: string;
}

@Component({
  selector: 'app-test-case-automation-card',
  imports: [
    TranslatePipe,
    DatePipe,
    RouterLink,
    MatIcon,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatProgressSpinner,
    ConfirmDialogComponent,
  ],
  templateUrl: './test-case-automation-card.component.html',
  styleUrl: './test-case-automation-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseAutomationCardComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly frameworkControl = input.required<FormControl<TestCaseAutomationFramework | null>>();
  readonly locatorControl = input.required<FormControl<string>>();
  readonly automationKey = input<string | null>(null);
  readonly projectId = input<string | null>(null);
  readonly testCaseId = input<string | null>(null);
  readonly testCaseTitle = input<string>('');
  /** Dieselbe Bedingung wie „Testfall ausführen“; ohne Schreibrecht kein Abbruch (NEX-1663). */
  readonly canWrite = input(false);

  readonly copied = signal(false);
  private readonly dialog = inject(MatDialog);
  private readonly executionJobs = inject(ExecutionJobService);

  /** Laufender bzw. zuletzt beendeter Auftrag; treibt die Fortschrittsanzeige. */
  readonly job = signal<ExecutionJob | null>(null);
  readonly jobError = signal<string | null>(null);

  /**
   * Ob gerade ein Auftrag beobachtet wird — der eigene oder der nachgeladene.
   *
   * <p>Nicht aus dem Status des Auftrags abgeleitet: Bricht die Verfolgung ab — Server
   * kurz weg, Anmeldung erneuert —, bleibt der letzte gemeldete Stand stehen und waere
   * fuer immer "laeuft". Der Kreisel drehte sich weiter, obwohl niemand mehr fragt, und
   * ein zweiter Versuch stiege an der Sperre unten still aus. Deshalb zaehlt, ob die
   * Verfolgung laeuft, nicht was zuletzt gemeldet wurde.
   */
  private readonly ownRunTracking = signal(false);
  private readonly loadedTracking = signal(false);

  readonly jobRunning = computed(() => this.ownRunTracking() || this.loadedTracking());

  private readonly loadingLatest = signal(false);
  readonly latestLoadFailed = signal(false);

  /**
   * Sperrt „Testfall ausführen“. Ein nachgeladener laufender Auftrag sperrt bewusst nicht:
   * Mehrere Aufträge je Testfall sind erlaubt, jeder auf seinem eigenen Rechner (NEX-1654).
   */
  readonly runBlocked = computed(() => this.ownRunTracking() || this.loadingLatest());

  readonly cancelConfirming = signal(false);
  readonly cancelling = signal(false);
  readonly cancelError = signal<CancelFailureKey | null>(null);
  /**
   * Merker je Auftrag: Eine Antwort der Verfolgung, die vor dem Abbruch abgeschickt wurde,
   * kommt noch ohne cancelRequested und darf den Zustand nicht zuruecksetzen.
   */
  private readonly cancelRequestedFor = signal<string | null>(null);

  readonly canCancel = computed(() => {
    const current = this.job();
    return this.canWrite() && !!current && !isFinalStatus(current.status);
  });

  readonly cancelRequested = computed(() => {
    const current = this.job();
    return (
      !!current &&
      !isFinalStatus(current.status) &&
      (current.cancelRequested === true || this.cancelRequestedFor() === current.id)
    );
  });

  private latestSubscription: Subscription | null = null;
  private startSubscription: Subscription | null = null;
  private followSubscription: Subscription | null = null;

  private readonly translate = inject(TranslateService);
  readonly locale = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  private readonly now = signal(Date.now());
  private readonly durationTicking = computed(() => {
    const current = this.job();
    return this.jobRunning() && !!current?.startedAt && !isFinalStatus(current.status);
  });

  readonly jobDuration = computed(() => {
    const current = this.job();
    if (!current?.startedAt) {
      return null;
    }
    const end = current.finishedAt ? Date.parse(current.finishedAt) : this.durationTicking() ? this.now() : NaN;
    const elapsed = end - Date.parse(current.startedAt);
    return Number.isNaN(elapsed) ? null : formatJobDuration(elapsed);
  });

  private copyResetHandle: ReturnType<typeof setTimeout> | null = null;

  readonly locatorMaxLength = FIELD_LENGTHS.AUTOMATION_LOCATOR_MAX;

  readonly frameworkOptions: readonly FrameworkOption[] = [
    { value: 'PLAYWRIGHT', labelKey: 'testCases.form.automation.framework.playwright' },
    { value: 'SELENIUM', labelKey: 'testCases.form.automation.framework.selenium' },
    { value: 'CYPRESS', labelKey: 'testCases.form.automation.framework.cypress' },
    { value: 'ROBOT_FRAMEWORK', labelKey: 'testCases.form.automation.framework.robotFramework' },
    { value: 'POSTMAN_NEWMAN', labelKey: 'testCases.form.automation.framework.postmanNewman' },
  ];

  openRunDialog(hasUnsavedChanges = false, owner: string | null = null): void {
    const data: RunTestCaseDialogData = {
      projectId: this.projectId() ?? '',
      subject: { kind: 'CASE', id: this.testCaseId() ?? '', key: this.automationKey(), title: this.testCaseTitle() },
      hasUnsavedChanges,
      owner,
    };

    openRunTestCaseDialog(this.dialog, data)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.kind === 'AUTOMATED' && result.job) {
          this.followStarted(result.job);
        }
      });
  }

  /**
   * Verfolgt den Auftrag, den der Dialog eingereiht hat, bis zum Endstand. Absagen beim Einreihen
   * zeigt der Dialog selbst (NEX-2412); hier kommt nur an, was angenommen wurde.
   */
  private followStarted(job: ExecutionJob): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    // Eine Antwort, die erst nach dem eigenen Start kaeme, wuerde den neuen Auftrag verdecken.
    this.latestSubscription?.unsubscribe();
    this.startSubscription?.unsubscribe();
    this.jobError.set(null);
    this.resetCancel();
    this.latestLoadFailed.set(false);
    this.startSubscription = this.follow(projectId, of(job), this.ownRunTracking);
  }

  /**
   * Gemeinsam fuer den eigenen Start und den nachgeladenen Auftrag, damit beide gleich enden.
   * Die vorige Verfolgung endet erst, wenn der erste Stand da ist: Lehnt der Server einen Start
   * ab, laeuft die Anzeige des nachgeladenen Auftrags weiter, statt auf „Läuft“ stehen zu bleiben.
   */
  private follow(projectId: string, first: Observable<ExecutionJob>, active: WritableSignal<boolean>): Subscription {
    const subscription = new Subscription();
    active.set(true);
    subscription.add(
      first
        .pipe(
          tap((job) => {
            if (this.followSubscription !== subscription) {
              this.followSubscription?.unsubscribe();
              this.followSubscription = subscription;
            }
            this.job.set(job);
          }),
          switchMap((job) => this.executionJobs.poll(projectId, job.id)),
          // Endstand, Fehler oder Seitenwechsel — in allen drei Faellen wird nicht mehr
          // beobachtet, und die Anzeige darf nichts anderes behaupten.
          finalize(() => active.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (job) => this.job.set(job),
          error: (error: unknown) => this.jobError.set(error instanceof PollTimeoutError ? 'pollGaveUp' : 'pollFailed'),
        }),
    );
    return subscription;
  }

  private loadLatest(projectId: string, testCaseId: string): void {
    // Dieselbe Karte kann einen anderen Testfall zeigen; vom vorigen darf nichts stehen bleiben.
    this.latestSubscription?.unsubscribe();
    this.startSubscription?.unsubscribe();
    this.followSubscription?.unsubscribe();
    this.job.set(null);
    this.jobError.set(null);
    this.resetCancel();
    this.latestLoadFailed.set(false);
    this.loadingLatest.set(true);
    this.latestSubscription = this.executionJobs
      .latestForTestCase(projectId, testCaseId)
      .pipe(
        finalize(() => this.loadingLatest.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (latest) => {
          if (!latest) {
            return;
          }
          if (isFinalStatus(latest.status)) {
            this.job.set(latest);
          } else {
            this.follow(projectId, of(latest), this.loadedTracking);
          }
        },
        error: () => this.latestLoadFailed.set(true),
      });
  }

  askCancel(): void {
    this.cancelError.set(null);
    this.cancelConfirming.set(true);
  }

  dismissCancel(): void {
    this.cancelConfirming.set(false);
  }

  /**
   * Ein wartender Auftrag endet sofort, ein laufender erst, wenn der Rechner aufhoert. Den
   * Endstand liefert die Verfolgung; lief sie nicht mehr, startet der Abbruch sie neu.
   */
  confirmCancel(): void {
    const projectId = this.projectId();
    const current = this.job();
    if (!projectId || !current || this.cancelling()) {
      return;
    }
    this.cancelConfirming.set(false);
    this.cancelling.set(true);
    this.executionJobs
      .cancel(projectId, current.id)
      .pipe(
        finalize(() => this.cancelling.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (answer) => {
          if (this.job()?.id !== answer.id) {
            return;
          }
          if (answer.cancelRequested) {
            this.cancelRequestedFor.set(answer.id);
          }
          this.job.set(answer);
          if (isFinalStatus(answer.status)) {
            // A poll sent before the cancel would otherwise reset the final state for a moment.
            this.followSubscription?.unsubscribe();
          } else if (!this.jobRunning()) {
            this.jobError.set(null);
            this.follow(projectId, of(answer), this.loadedTracking);
          }
        },
        error: (error: unknown) => {
          const key = cancelFailureKey(error);
          this.cancelError.set(key);
          if (key === 'alreadyFinished') {
            this.reloadJob(projectId, current.id);
          }
        },
      });
  }

  private reloadJob(projectId: string, jobId: string): void {
    this.executionJobs
      .get(projectId, jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          if (this.job()?.id === job.id) {
            this.job.set(job);
          }
        },
        error: () => this.jobError.set('pollFailed'),
      });
  }

  private resetCancel(): void {
    this.cancelConfirming.set(false);
    this.cancelError.set(null);
    this.cancelRequestedFor.set(null);
  }

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      const testCaseId = this.testCaseId();
      if (projectId && testCaseId) {
        untracked(() => this.loadLatest(projectId, testCaseId));
      }
    });
    effect((onCleanup) => {
      if (!this.durationTicking()) {
        return;
      }
      this.now.set(Date.now());
      const handle = setInterval(() => this.now.set(Date.now()), DURATION_TICK_MS);
      onCleanup(() => clearInterval(handle));
    });
    this.destroyRef.onDestroy(() => this.clearCopyReset());
  }

  copyKey(): void {
    const key = this.automationKey();
    if (!key) {
      return;
    }
    void copyText(key).then((copied) => {
      if (copied) {
        this.flagCopied();
      }
    });
  }

  private flagCopied(): void {
    this.copied.set(true);
    this.clearCopyReset();
    this.copyResetHandle = setTimeout(() => this.copied.set(false), 1400);
  }

  private clearCopyReset(): void {
    if (this.copyResetHandle !== null) {
      clearTimeout(this.copyResetHandle);
      this.copyResetHandle = null;
    }
  }
}
